import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

import { CountryModel, VerificationDataRequirements } from 'ng-voxloud';

import { environment } from 'src/environments/environment';
import {
  DID_TYPE,
  DID_CLASS,
  VerificationData,
  OrderDidRequest,
  DidsByCompanyIds,
  DID,
} from 'voxloud-types/dist/models';
import { DidsService, VerificationDataService } from 'src/app/domains/dids/services';
import { OPTION_TYPE, VerificationDataSelection } from 'src/app/shared/components';
import { concatMap } from 'rxjs/operators';
import { EstimationRequest, PRODUCT_TYPE } from 'src/app/domains/payment/models/payment.model';
import { Subscription } from 'src/app/domains/subscription/models';
import { Observable } from 'rxjs';
import { OrderDidManuallyRequest, OrderDidManuallyResponse } from 'src/app/domains/dids/models';

@Component({
  selector: 'gdp-create-did-order-popup',
  templateUrl: './create-did-order-popup.component.html',
  styleUrls: ['./create-did-order-popup.component.scss']
})
export class CreateDidOrderPopupComponent implements OnInit {
  public step: 'did-data' | 'recap';
  public subscription: Subscription;
  public companyId: number;
  public dids: DID[] = [];
  public allowNoVD: boolean = true;
  public requirements: VerificationDataRequirements;
  public country: string;
  public availableCountries: CountryModel[] = environment.servedCountries;
  public form: UntypedFormGroup;
  public availableClasses = Object.keys(DID_CLASS);
  public availableTypes = Object.keys(DID_TYPE);
  public loading = {
    get: false,
    submit: false,
    vdData: false
  };
  public vdForm: VerificationDataSelection = {
    value: null,
    isValid: false,
    type: null,
    selectedOpt: { type: OPTION_TYPE.NEW, did: null }
  };
  public checkboxesData = {
    value: null,
    isValid: false
  };
  public isVDRequired: boolean;
  public currentLanguage: string;
  @Output() onCreateDid: EventEmitter<string> = new EventEmitter();
  public product: EstimationRequest;
  public planAddonHandling: string;

  public vdRequirements: {
    [key: string]: boolean
  } = {}

  constructor(
    private activeModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private didsService: DidsService,
    private verificationDataService: VerificationDataService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.step = 'did-data';
    this.currentLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe((l: LangChangeEvent) => this.currentLanguage = l.lang);

    this.form = this.fb.group({
      country: [null],
      class: [DID_CLASS.VOICE, [Validators.required]],
      type: [DID_TYPE.GEOGRAPHIC, [Validators.required]],
      prefix: [null, [Validators.required]],
      couponId: [null]
    });
    this.getInitialValues();
    this.form.controls.country.valueChanges.subscribe((vg: CountryModel) => {
      this.country = vg.iso2;
      this.loading.vdData = true;
      this.verificationDataService.getVerificationDataRequirementsByCountry(vg.iso2).subscribe((v: VerificationDataRequirements) => {
        this.requirements = v;
        this.isVDRequired = !!(v.BUSINESS || v.PRIVATE);
        this.vdRequirements[vg.iso2] = !!(v.BUSINESS || v.PRIVATE);
        this.loading.vdData = false;
      });
    });
  }

  public getInitialValues(): void {
    this.loading.get = true;
    this.didsService.getDidsByCompanyIds(this.companyId).subscribe((res: DidsByCompanyIds[]) => {
      this.dids = res[0].dids.filter((did) => did.verification_data_id !== undefined);
      this.loading.get = false;
    }, () => {
      this.loading.get = false;
    });
  }

  public onFormChange(event: VerificationDataSelection): void {
    this.vdForm = {
      ...event
    };
  }

  public onGoNext(): void {
    const formValue = this.form.value;
    this.product = {
      product: formValue.class === DID_CLASS.VOICE ? PRODUCT_TYPE.phone_number : PRODUCT_TYPE.fax,
      default_addon: formValue.class === DID_CLASS.VOICE ? PRODUCT_TYPE.phone_number : PRODUCT_TYPE.fax,
      country_code: this.country.toLowerCase(),
      did_type: formValue.type,
      coupon_id: formValue.couponId ? formValue.couponId : null
    };
    this.step = 'recap';
  }

  public onGoBack(): void {
    this.step = 'did-data';
    this.checkboxesData.isValid = false;
  }

  public isDisabled(): boolean {
    let result: boolean;
    if (this.isVDRequired) {
      result = this.form.invalid || !this.vdForm.isValid;
    } else {
      result = this.form.invalid || !this.checkboxesData.isValid;
    }
    return result;
  }

  public isFieldValid(name: string): boolean {
    return (
      !this.form.get(name).valid && this.form.get(name).touched
    );
  }

  public selectClass(classes: string): void {
    this.form.get('class').setValue(classes);
  }

  public selectType(type: string): void {
    this.form.get('type').setValue(type);
  }

  public onSubmit(): void {
    if (this.form.valid) {
      const value = this.form.value;
      let payload: OrderDidRequest | OrderDidManuallyRequest = {
        area_code: value.prefix.code,
        country_code: value.country.iso2,
        did_type: value.type,
        did_class: value.class,
        verification_data_id: undefined,
        source_verification_data_id: undefined
      }
      // if addon_management: 'manual' we need to add company_id in the payload
      // cause we don't have it in call url
      if (this.planAddonHandling) {
        payload = {
          ...payload,
          company_id: this.companyId
        };
      }
      if (this.isVDRequired) {
        switch (this.vdForm.selectedOpt.type) {
          case OPTION_TYPE.NEW: {
            this.createVDAndOredrDid(payload);
          };
            break;
          case OPTION_TYPE.CLONE: {
            payload.source_verification_data_id = this.vdForm.selectedOpt.did.verification_data_id;
            this.orderRequest(payload);
          };
            break;
          case OPTION_TYPE.NONE: {
            this.orderRequest(payload);
          };
            break;
          default: break;
        }
      } else {
        this.orderRequest(payload);
      }
    }
  }

  // Helper methods
  private createVDAndOredrDid(payload: OrderDidRequest | OrderDidManuallyRequest): void {
    this.loading.submit = true;
    const vdPayload: FormData = this.verificationDataService.buildVerificationData(
      this.companyId,
      this.country,
      {
        type: this.vdForm.type,
        data: this.requirements,
        value: this.vdForm
      }
    );
    this.verificationDataService.createVerificationData(vdPayload).pipe(
      concatMap((res: VerificationData) => {
        payload.verification_data_id = res.id;
        return this.orderRequestWithVDData(payload)
      })
      // type of response is any because it can be both string or OrderDidManuallyResponse
    ).subscribe((res: any) => {
      const emitValue: string = this.planAddonHandling === 'manual' ? res.did.number : res;
      this.onCreateDid.emit(emitValue);
      this.requestDone();
    }, () => {
      this.requestDone();
    });
  }

  // return Observable to use in concatMap
  private orderRequestWithVDData(payload: OrderDidRequest | OrderDidManuallyRequest): Observable<any> {
    return this.planAddonHandling === 'manual'
      ? this.didsService.orderDidManually(<OrderDidManuallyRequest>payload)
      : this.didsService.orderDid(this.companyId, payload);
  }

  private orderRequest(payload: OrderDidRequest | OrderDidManuallyRequest): void {
    this.loading.submit = true;
    if(this.planAddonHandling === 'manual') {
      this.didsService.orderDidManually(<OrderDidManuallyRequest>payload).subscribe((res: OrderDidManuallyResponse) => {
        this.onCreateDid.emit(res.did.number);
        this.requestDone();
      }, () => {
        this.requestDone();
      });
    } else {
      this.didsService.orderDid(this.companyId, payload).subscribe((res: string) => {
        this.onCreateDid.emit(res);
        this.requestDone();
      }, () => {
        this.requestDone();
      });
    }
  }

  private requestDone(): void {
    this.loading.submit = false;
    this.close();
  }

  public close() {
    this.activeModal.close();
  }
}
